import { useState } from "react";
import { Autocomplete, Box, Button, Stack, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import { PlusIcon } from "../../icons";
import { ErrorMessage } from "../../../dynamicForm/inlineForm/ErrorMessage";
import { AUTOCOMPLETE_LOADING_OPTION, AutocompleteOption, type AutocompleteOptionType } from "../../Autocomplete";
import { logError } from "../../../api/logError";

const MAX_WIDTH = "250px";

type AddConnectionOption = { id: string; name: string };

type AddConnectionProps = {
  /** Options to display in the autocomplete. Pass `undefined` to render the loading state. */
  excludeIds?: Array<AddConnectionOption["id"]>;
  options?: Array<AddConnectionOption>;
  onMouseEnter: () => void;
  onFocus: () => void;
  onAdd: (option: AddConnectionOption) => Promise<unknown>;
  searchPlaceholder: string;
};

export const AddConnection = (props: AddConnectionProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const addMutation = useMutation({ mutationFn: props.onAdd, onError: () => setIsOpen(false) });

  const excludeIds = new Set(props.excludeIds);
  const autocompleteOptions: AutocompleteOptionType[] = props.options
    ? props.options.filter(o => !excludeIds.has(o.id)).map(({ name, id }) => ({ label: name, value: id }))
    : [AUTOCOMPLETE_LOADING_OPTION];

  if (!isOpen) {
    return (
      <Stack sx={{ gap: 0.5, maxWidth: MAX_WIDTH }}>
        {addMutation.isError && <ErrorMessage errorMessage={t("Drawer.Connections.Error.Generic")} />}
        <Button
          variant="text"
          startIcon={<PlusIcon size={14} />}
          aria-disabled={addMutation.isPending}
          onClick={() => {
            if (addMutation.isPending) return;
            addMutation.reset();
            setIsOpen(true);
          }}
          onMouseEnter={props.onMouseEnter}
          onFocus={props.onFocus}
          sx={theme => ({
            "alignSelf": "flex-start",
            // Match the autocomplete input height to avoid layout shift when toggling.
            "height": 36,
            "px": 2,
            "fontWeight": 600,
            "fontSize": theme.typography.pxToRem(12),
            "&:hover": { backgroundColor: theme.palette.action.hover },
            "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
          })}
        >
          {t("Drawer.Connections.AddButton")}
        </Button>
      </Stack>
    );
  }

  return (
    <Box sx={{ maxWidth: MAX_WIDTH }}>
      <Autocomplete
        openOnFocus
        size="small"
        options={autocompleteOptions}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, selected) =>
          option.value === AUTOCOMPLETE_LOADING_OPTION.value || option.value === selected.value
        }
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return <AutocompleteOption key={option.value} option={option} {...optionProps} />;
        }}
        noOptionsText={t("Drawer.Connections.NoResults")}
        disabled={addMutation.isPending}
        onChange={(_, value) => {
          if (!value || value.value === AUTOCOMPLETE_LOADING_OPTION.value) return;
          const selected = props.options?.find(o => o.id === value.value);
          if (!selected) {
            logError(new Error(`AddConnection: selected option ${value.value} not found in props.options`), {
              loggedFrom: "AddConnection",
            });
            return;
          }

          setIsOpen(false);
          addMutation.mutate(selected);
        }}
        onClose={(_, reason) => {
          if (reason === "escape" || reason === "blur") setIsOpen(false);
        }}
        renderInput={params => (
          <TextField
            {...params}
            autoFocus
            placeholder={props.searchPlaceholder}
            onKeyDown={e => {
              if (e.key === "Escape") setIsOpen(false);
            }}
          />
        )}
      />
    </Box>
  );
};
